import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PenTool, Fingerprint, Image as ImageIcon, Share2, Wand2, Clapperboard, Play, Sparkles, UploadCloud, X, ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Eyebrow } from "@/components/Reveal";

const TYPES = [
  { id: "logo", label: "Logo", icon: PenTool },
  { id: "branding", label: "Branding", icon: Fingerprint },
  { id: "graphic-design", label: "Graphic", icon: ImageIcon },
  { id: "creator-content", label: "Social content", icon: Share2 },
  { id: "image-editing", label: "Image editing", icon: Wand2 },
  { id: "video-editing", label: "Video editing", icon: Clapperboard },
  { id: "motion", label: "Motion graphics", icon: Play },
  { id: "custom", label: "Custom", icon: Sparkles },
];

const BUDGETS = ["Under $100", "$100–$250", "$250–$500", "$500–$1,000", "$1,000+", "Not Sure", "Subscription"];
const STEPS = ["WHAT", "DETAILS", "FILES", "BUDGET", "CONTACT", "SEND"];

export default function StartProject() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    type: "", title: "", description: "", intended_use: "", style: "",
    required_text: "", colours: "", references: "", deadline: "", budget: "",
    contact: { name: "", email: "", discord: "", company: "" },
  });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    const t = params.get("type");
    const plan = params.get("plan");
    setForm((f) => ({
      ...f,
      type: t && TYPES.some((x) => x.id === t) ? t : f.type,
      budget: plan ? "Subscription" : f.budget,
    }));
  }, [params]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, contact: { ...f.contact, name: f.contact.name || user.name, email: f.contact.email || user.email, company: f.contact.company || user.company || "" } }));
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));

  const canNext = useMemo(() => {
    if (step === 0) return !!form.type;
    if (step === 1) return form.title.trim().length > 1 && form.description.trim().length > 5;
    if (step === 2) return true;
    if (step === 3) return !!form.budget;
    if (step === 4) return form.contact.name.trim() && /.+@.+\..+/.test(form.contact.email);
    return true;
  }, [step, form]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(form));
      files.forEach((f) => fd.append("files", f));
      await api.post("/requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="pt-32 pb-28 px-5" data-testid="request-confirmation">
        <Seo title="Request received" path="/start-a-project" />
        <div className="max-w-xl mx-auto text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-arsenal" />
          <h1 className="mt-6 font-display text-3xl sm:text-4xl font-black tracking-tight">PROJECT REQUEST RECEIVED</h1>
          <div className="mt-8 text-left border border-white/10 bg-ink-surface p-7 space-y-4">
            <p className="font-mono text-[11px] tracking-[0.25em] text-arsenal">WHAT HAPPENS NEXT</p>
            {[
              "We review your brief and anything you uploaded.",
              "You get a reply with a quote or questions — usually within a couple of business days.",
              user ? "Track progress in your client portal once the project is accepted." : "Create a client portal account with the same email to track progress once accepted.",
              "Work begins, you review, we refine, you download.",
            ].map((s, i) => (
              <div key={i} className="flex gap-3 text-sm text-slate-300">
                <span className="font-mono text-arsenal">{String(i + 1).padStart(2, "0")}</span> {s}
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-center gap-4">
            {user && (
              <Link to="/portal" data-testid="confirmation-portal-link" className="clip-btn bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors">GO TO PORTAL</Link>
            )}
            <Link to="/" data-testid="confirmation-home-link" className="clip-btn border border-white/20 hover:border-arsenal text-white font-display font-bold tracking-widest text-xs px-6 py-3 transition-colors">BACK HOME</Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className="pt-24 sm:pt-28 pb-24" data-testid="start-project-page">
      <Seo title="Start a Project" description="Brief Arsenal Artists in minutes — logos, branding, graphics, image editing, video or something custom." path="/start-a-project" />
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Eyebrow>START A PROJECT</Eyebrow>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Brief us in <span className="text-arsenal">five minutes.</span></h1>

        {/* progress */}
        <div className="mt-10 flex items-center gap-1.5" data-testid="wizard-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 transition-colors duration-300 ${i <= step ? "bg-arsenal" : "bg-white/10"}`} />
              <p className={`mt-2 font-mono text-[9px] tracking-[0.15em] ${i <= step ? "text-arsenal" : "text-slate-600"}`}>{s}</p>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25 }}
            className="mt-10"
          >
            {step === 0 && (
              <div data-testid="wizard-step-type">
                <h2 className="font-display text-xl font-bold mb-6">What do you need?</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {TYPES.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`type-${t.id}`}
                      onClick={() => set("type", t.id)}
                      className={`flex flex-col items-center gap-3 border p-5 transition-colors ${
                        form.type === t.id ? "border-arsenal bg-arsenal/10 text-white" : "border-white/10 bg-ink-surface text-slate-400 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      <t.icon className={`h-6 w-6 ${form.type === t.id ? "text-arsenal" : ""}`} />
                      <span className="text-sm font-semibold text-center">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5" data-testid="wizard-step-details">
                <h2 className="font-display text-xl font-bold">Tell us about it</h2>
                <Field label="Project title *"><input data-testid="field-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder='e.g. "Nova Streams channel rebrand"' className="aa-input" /></Field>
                <Field label="Description *"><textarea data-testid="field-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} placeholder="What is it, who is it for, what should it achieve?" className="aa-input" /></Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Intended use"><input data-testid="field-use" value={form.intended_use} onChange={(e) => set("intended_use", e.target.value)} placeholder="Website, Discord, print, ads…" className="aa-input" /></Field>
                  <Field label="Style / preferences"><input data-testid="field-style" value={form.style} onChange={(e) => set("style", e.target.value)} placeholder="Minimal, grungy, corporate, anime…" className="aa-input" /></Field>
                  <Field label="Required text"><input data-testid="field-text" value={form.required_text} onChange={(e) => set("required_text", e.target.value)} placeholder="Exact wording that must appear" className="aa-input" /></Field>
                  <Field label="Colours"><input data-testid="field-colours" value={form.colours} onChange={(e) => set("colours", e.target.value)} placeholder="Brand colours or hex codes" className="aa-input" /></Field>
                </div>
                <Field label="References (links)"><textarea data-testid="field-references" value={form.references} onChange={(e) => set("references", e.target.value)} rows={2} placeholder="Links to styles, competitors or inspiration" className="aa-input" /></Field>
                <Field label="Deadline"><input data-testid="field-deadline" type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className="aa-input" /></Field>
              </div>
            )}

            {step === 2 && (
              <div data-testid="wizard-step-files">
                <h2 className="font-display text-xl font-bold mb-2">Upload files</h2>
                <p className="text-sm text-slate-500 mb-6">Images, logos, videos, documents, reference material — up to 10 files. Optional.</p>
                <label
                  data-testid="file-dropzone"
                  className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-arsenal/60 bg-ink-surface p-10 cursor-pointer transition-colors"
                >
                  <UploadCloud className="h-8 w-8 text-arsenal" />
                  <span className="text-sm text-slate-400">Click to choose files</span>
                  <input
                    type="file"
                    multiple
                    data-testid="file-input"
                    className="hidden"
                    onChange={(e) => setFiles((f) => [...f, ...Array.from(e.target.files)].slice(0, 10))}
                  />
                </label>
                {files.length > 0 && (
                  <ul className="mt-5 space-y-2" data-testid="file-list">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-white/10 bg-ink-surface px-4 py-2.5 text-sm">
                        <span className="text-slate-300 truncate">{f.name} <span className="text-slate-600">({(f.size / 1024).toFixed(0)} KB)</span></span>
                        <button data-testid={`file-remove-${i}`} onClick={() => setFiles((fl) => fl.filter((_, j) => j !== i))} className="text-slate-500 hover:text-arsenal"><X className="h-4 w-4" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {step === 3 && (
              <div data-testid="wizard-step-budget">
                <h2 className="font-display text-xl font-bold mb-6">Budget</h2>
                <div className="flex flex-wrap gap-3">
                  {BUDGETS.map((b) => (
                    <button
                      key={b}
                      data-testid={`budget-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => set("budget", b)}
                      className={`clip-tag border px-5 py-3 text-sm font-semibold transition-colors ${
                        form.budget === b ? "border-arsenal bg-arsenal text-white" : "border-white/15 text-slate-300 hover:border-arsenal/60"
                      }`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
                <p className="mt-5 text-sm text-slate-500">A rough range is enough — your quote confirms the final price before any work starts.</p>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5" data-testid="wizard-step-contact">
                <h2 className="font-display text-xl font-bold">Where do we reach you?</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Name *"><input data-testid="contact-name" value={form.contact.name} onChange={(e) => setContact("name", e.target.value)} className="aa-input" /></Field>
                  <Field label="Email *"><input data-testid="contact-email" type="email" value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} className="aa-input" /></Field>
                  <Field label="Discord (if applicable)"><input data-testid="contact-discord" value={form.contact.discord} onChange={(e) => setContact("discord", e.target.value)} className="aa-input" /></Field>
                  <Field label="Business / company"><input data-testid="contact-company" value={form.contact.company} onChange={(e) => setContact("company", e.target.value)} className="aa-input" /></Field>
                </div>
              </div>
            )}

            {step === 5 && (
              <div data-testid="wizard-step-review">
                <h2 className="font-display text-xl font-bold mb-6">Review & send</h2>
                <dl className="border border-white/10 bg-ink-surface divide-y divide-white/5">
                  {[
                    ["Type", TYPES.find((t) => t.id === form.type)?.label],
                    ["Project", form.title],
                    ["Description", form.description],
                    ["Budget", form.budget],
                    ["Files", files.length ? files.map((f) => f.name).join(", ") : "None"],
                    ["Contact", `${form.contact.name} — ${form.contact.email}`],
                    form.deadline && ["Deadline", form.deadline],
                  ].filter(Boolean).map(([k, v]) => (
                    <div key={k} className="grid sm:grid-cols-[140px_1fr] gap-1 px-5 py-3.5">
                      <dt className="font-mono text-[11px] tracking-[0.15em] text-slate-500 uppercase">{k}</dt>
                      <dd className="text-sm text-slate-200 break-words">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <button
            data-testid="wizard-back"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> BACK
          </button>
          {step < 5 ? (
            <button
              data-testid="wizard-next"
              onClick={() => canNext && setStep((s) => s + 1)}
              disabled={!canNext}
              className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright disabled:opacity-40 text-white font-display font-bold tracking-widest text-sm px-8 py-3.5 transition-colors"
            >
              NEXT <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              data-testid="wizard-submit"
              onClick={submit}
              disabled={submitting}
              className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright disabled:opacity-50 text-white font-display font-bold tracking-widest text-sm px-8 py-3.5 transition-colors shadow-[0_0_30px_rgba(229,9,20,0.35)]"
            >
              {submitting ? "SENDING…" : "SUBMIT REQUEST"} <ArrowUpRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[11px] tracking-[0.2em] text-slate-500 uppercase">{label}</span>
      {children}
    </label>
  );
}
